/**
 * ProjectTableView component - Table-based view for projects list
 * 
 * Displays projects in a sortable table format with columns for:
 * - Project name and description
 * - Manager and programme
 * - Status and priority
 * - Metrics (tasks, completion, overdue)
 * - Action buttons (View, Edit, Delete)
 */

import React from 'react';
import { Eye, Edit, Trash2, Users as UsersIcon, Plus, Milestone as MilestoneIcon, ListTodo } from 'lucide-react';
import DataTable, { TableColumn } from './DataTable';
import Button from './Button';
import Badge from './Badge';
import { Project } from '../types/project';
import { Task } from '../types/task';
import { User } from '../types/user';
import { Programme } from '../api/programmes';
import { Milestone } from '../types/milestone';

interface ProjectTableRow {
  id: string;
  name: string;
  description?: string;
  manager: string;
  programme: string;
  priority: string;
  status: string;
  totalTasks: number;
  completedTasks: number;
  completionRate: number;
  overdueTasks: number;
  milestoneCount: number;
  createdAt: string;
}

interface ProjectTableViewProps {
  projects: Project[];
  tasks: Task[];
  users: User[];
  programmes: Programme[];
  milestones: Milestone[];
  onView: (projectId: string) => void;
  onEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
  onManageRoles: (projectId: string) => void;
  onAddTask?: (projectId: string) => void;
  onAddMilestone?: (projectId: string) => void;
  onViewTasks?: (projectId: string) => void;
  onViewMilestones?: (projectId: string) => void;
  canEdit: (projectId: string) => boolean;
  canManageRoles?: boolean;
}

const ProjectTableView: React.FC<ProjectTableViewProps> = ({
  projects,
  tasks,
  users,
  programmes,
  milestones,
  onView,
  onEdit,
  onDelete,
  onManageRoles,
  onAddTask,
  onAddMilestone,
  onViewTasks,
  onViewMilestones,
  canEdit,
  canManageRoles = false,
}) => {
  // Helper functions
  const getUserName = (userId?: string | null): string => {
    if (!userId) return 'Unassigned';
    const user = users.find(u => u.id === userId);
    return user ? (user.full_name || user.username) : 'Unknown';
  };

  const getProgrammeName = (programmeId?: string | null): string => {
    if (!programmeId) return 'No Programme';
    const programme = programmes.find(p => p.id === programmeId);
    return programme ? programme.name : 'Unknown';
  };

  const calculateProjectMetrics = (projectId: string) => {
    const projectTasks = tasks.filter(t => t.project_id === projectId);
    const totalTasks = projectTasks.length;
    const completedTasks = projectTasks.filter(t => t.status === 'completed').length;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    
    const now = new Date();
    const overdueTasks = projectTasks.filter(t => 
      t.due_date && 
      new Date(t.due_date) < now && 
      t.status !== 'completed'
    ).length;

    const projectMilestones = milestones.filter(m => m.project_id === projectId);
    
    return { totalTasks, completedTasks, completionRate, overdueTasks, milestoneCount: projectMilestones.length };
  };

  const getPriorityBadge = (priority?: string) => {
    return (
      <Badge priority={(priority || 'low') as any} size="sm">
        {priority || 'low'}
      </Badge>
    );
  };

  const getStatusBadge = (status?: string) => {
    const statusMap: Record<string, string> = {
      planning: 'Planning',
      active: 'Active',
      on_hold: 'On Hold',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    
    const label = statusMap[status || 'active'] || statusMap.active;
    
    return (
      <Badge status={(status || 'active') as any} size="sm">
        {label}
      </Badge>
    );
  };

  // Transform projects into table rows
  const tableData: ProjectTableRow[] = projects.map(project => {
    const metrics = calculateProjectMetrics(project.id);
    return {
      id: project.id,
      name: project.name,
      description: project.description ?? undefined,
      manager: getUserName(project.manager_id),
      programme: getProgrammeName(project.programme_id),
      priority: project.priority || 'low',
      status: project.status || 'active',
      totalTasks: metrics.totalTasks,
      completedTasks: metrics.completedTasks,
      completionRate: metrics.completionRate,
      overdueTasks: metrics.overdueTasks,
      milestoneCount: metrics.milestoneCount,
      createdAt: project.created_at,
    };
  });

  // Define table columns
  const columns: TableColumn<ProjectTableRow>[] = [
    {
      key: 'name',
      label: 'Project Name',
      sortable: true,
      className: 'font-medium text-gray-900 dark:text-white min-w-[200px]',
      render: (row) => (
        <div>
          <div className="font-semibold">{row.name}</div>
          {row.description && (
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
              {row.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'manager',
      label: 'Manager',
      sortable: true,
      className: 'text-sm',
      render: (row) => (
        <span className="text-gray-700 dark:text-gray-300">{row.manager}</span>
      ),
    },
    {
      key: 'programme',
      label: 'Programme',
      sortable: true,
      className: 'text-sm',
      render: (row) => (
        <span className="text-gray-700 dark:text-gray-300">{row.programme}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => getStatusBadge(row.status),
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      render: (row) => getPriorityBadge(row.priority),
    },
    {
      key: 'totalTasks',
      label: 'Tasks',
      sortable: true,
      sortValue: (row) => row.totalTasks,
      className: 'text-center',
      render: (row) => (
        <div className="text-sm">
          <div className="font-semibold text-gray-900 dark:text-white">
            {row.completedTasks}/{row.totalTasks}
          </div>
          {row.overdueTasks > 0 && (
            <div className="text-xs text-red-600 dark:text-red-400">
              {row.overdueTasks} overdue
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'completionRate',
      label: 'Progress',
      sortable: true,
      sortValue: (row) => row.completionRate,
      className: 'text-center min-w-[120px]',
      render: (row) => (
        <div className="space-y-1">
          <div className="text-sm font-semibold text-gray-900 dark:text-white">
            {row.completionRate}%
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${row.completionRate}%` }}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'milestoneCount',
      label: 'Milestones',
      sortable: true,
      sortValue: (row) => row.milestoneCount,
      className: 'text-center',
      render: (row) => (
        <span className="text-sm text-gray-700 dark:text-gray-300">
          {row.milestoneCount}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'min-w-[280px]',
      render: (row) => {
        const project = projects.find(p => p.id === row.id)!;
        return (
          <div className="flex gap-1 justify-end flex-wrap">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={Eye}
              onClick={() => onView(row.id)}
              title="View Details"
            >
              View
            </Button>
            {onViewTasks && row.totalTasks > 0 && (
              <Button
                variant="accent"
                size="sm"
                leftIcon={ListTodo}
                onClick={() => onViewTasks(row.id)}
                title="View Tasks"
              >
                Tasks ({row.totalTasks})
              </Button>
            )}
            {onViewMilestones && row.milestoneCount > 0 && (
              <Button
                variant="accent"
                size="sm"
                leftIcon={MilestoneIcon}
                onClick={() => onViewMilestones(row.id)}
                title="View Milestones"
              >
                Milestones ({row.milestoneCount})
              </Button>
            )}
            {canEdit(row.id) && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={Edit}
                  onClick={() => onEdit(project)}
                  title="Edit Project"
                >
                  Edit
                </Button>
                {onAddTask && (
                  <Button
                    variant="success"
                    size="sm"
                    leftIcon={Plus}
                    onClick={() => onAddTask(row.id)}
                    title="Add Task"
                  >
                    Task
                  </Button>
                )}
                {onAddMilestone && (
                  <Button
                    variant="success"
                    size="sm"
                    leftIcon={MilestoneIcon}
                    onClick={() => onAddMilestone(row.id)}
                    title="Add Milestone"
                  >
                    Milestone
                  </Button>
                )}
              </>
            )}
            {canManageRoles && (
              <Button
                variant="outline"
                size="sm"
                leftIcon={UsersIcon}
                onClick={() => onManageRoles(row.id)}
                title="Manage Team Roles"
              >
                Team
              </Button>
            )}
            {canEdit(row.id) && (
              <Button
                variant="danger"
                size="sm"
                leftIcon={Trash2}
                onClick={() => onDelete(project)}
                title="Delete Project"
              >
                Delete
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-white/20 dark:border-gray-700/50 rounded-xl shadow-lg p-6">
      <DataTable
        columns={columns}
        data={tableData}
        emptyMessage="No projects found"
        striped={true}
        getRowKey={(row) => row.id}
        defaultSortKey="name"
        defaultSortDirection="asc"
        onRowClick={(row) => onView(row.id)}
      />
    </div>
  );
};

export default ProjectTableView;
